// Temporary diagnostics for the upload on step 1 of the request form, which sometimes stalls at a
// visitor: the progress bar stops moving and — according to the visitor's own dev tools — no
// request for `formie/file-upload/upload` is ever made.
//
// Nothing about such an attempt is recorded today. Formie drives the field with a headless Uppy
// whose logger it never enables, and swallows the three failure paths that would explain it: a
// file refused on size or type goes to `uppy.info()`, which has no UI attached; the promise behind
// `autoProceed` is caught and dropped; and `upload()` waits on an earlier upload that may never
// finish. All three leave an item sitting on "Preparing…" forever.
//
// So this module watches from the outside: it wraps `XMLHttpRequest` before Formie mounts, keeps a
// timeline per attempt, and posts it to `wat-is-het-waard/diagnostics/upload-log`. The DOM half of
// the story — what the file list does — is fed in by `requestUpload.js`, which also acts on it.
//
// Remove this file, its callers and the server-side half once the cause is known.

const endpointsSelector = '[data-request-endpoints]'
// No leading slash: `UrlHelper::actionUrl()` keeps `index.php` in the URL whenever `pathParam` is
// set, so Formie's endpoint arrives as `index.php?p=actions/formie/file-upload/upload` — matching
// on `/actions/…` would miss every request.
const uploadUrlFragment = 'formie/file-upload/'
const maxEvents = 80
const progressStep = 10
const progressInterval = 2000
const cookieMaxAge = 1800

// A second `upload-manager:init` on one page would mean two Uppy instances on the same field —
// one of the ways an item can end up waiting on an upload that is not its own.
const observedFormEvents = [
    'formie:module:upload-manager:init',
    'formie:module:upload-manager:destroy',
    'formie:file-upload:uploaded-assets-sync',
    'formie:submit:before',
    'formie:page:navigate:after'
]

// One attempt is everything that follows a single trip to the file picker.
let attempt = null

const debugEnabled = () => {
    try {
        return (
            window.location.search.includes('uploadDebug=1') ||
            window.localStorage.getItem('whwUploadDebug') === '1'
        )
    } catch {
        return false
    }
}

const short = (value, length = 160) =>
    typeof value === 'string' && value.length > length
        ? `${value.slice(0, length)}…`
        : (value ?? '')

// Formie hands its events details that reach back into the DOM — `formie:submit:before` carries
// the form, which carries its validator, which carries the form again. A plain `JSON.stringify`
// throws on that, and it threw inside Formie's own emitter. DOM nodes become their tag name and
// anything already seen becomes a marker, so the summary can never be the thing that breaks.
const describeDetail = (detail) => {
    if (detail === null || detail === undefined) {
        return ''
    }

    if (typeof detail !== 'object') {
        return String(detail)
    }

    const seen = new WeakSet()

    try {
        return JSON.stringify(detail, (key, value) => {
            if (value instanceof Node) {
                return `<${value.nodeName.toLowerCase()}>`
            }

            if (typeof value === 'function') {
                return '[function]'
            }

            if (typeof value === 'object' && value !== null) {
                if (seen.has(value)) {
                    return '[circular]'
                }

                seen.add(value)
            }

            return value
        })
    } catch {
        return '[not serialisable]'
    }
}

// Mirrors the id into a cookie, which is the only channel the upload request itself carries: the
// server-side half reads it there and both timelines end up under the same id in the log.
const setCorrelationCookie = (id) => {
    try {
        const secure = window.location.protocol === 'https:' ? '; Secure' : ''
        document.cookie = `whwUpload=${id}; path=/; max-age=${cookieMaxAge}; SameSite=Lax${secure}`
    } catch {
        // A blocked cookie only costs us the link between the two halves.
    }
}

const describeContext = () => {
    const connection = navigator.connection
    const parts = [
        `online=${navigator.onLine}`,
        `screen=${window.innerWidth}x${window.innerHeight}`
    ]

    if (connection) {
        parts.push(
            `conn=${connection.effectiveType || '?'}/${connection.downlink ?? '?'}Mbps`,
            `save-data=${connection.saveData === true}`
        )
    }

    return parts.join(' ')
}

export const startUploadAttempt = (detail = '') => {
    const id = Math.random().toString(36).slice(2, 8)

    attempt = {
        id,
        startedAt: Date.now(),
        events: [],
        sent: 0,
        truncated: false,
        requests: 0,
        lastProgressAt: 0
    }

    setCorrelationCookie(id)
    recordUploadEvent('attempt:start', detail)

    return id
}

export const recordUploadEvent = (name, detail = '') => {
    if (!attempt) {
        return
    }

    if (attempt.events.length >= maxEvents) {
        attempt.truncated = true

        return
    }

    attempt.events.push({
        t: Date.now() - attempt.startedAt,
        e: name,
        d: short(typeof detail === 'string' ? detail : String(detail))
    })

    if (debugEnabled()) {
        console.info(
            `[upload ${attempt.id}] +${Date.now() - attempt.startedAt}ms ${name}`,
            detail
        )
    }
}

/**
 * What the browser did with the upload endpoint so far. The watchdogs in `requestUpload.js` use
 * this to tell "the request is slow" apart from "the request was never made" — the difference that
 * decides whether the problem is in Formie's client or beyond it.
 */
export const uploadRequestStats = () => ({
    requests: attempt?.requests ?? 0,
    lastProgressAt: attempt?.lastProgressAt ?? 0
})

export const flushUploadReport = (reason) => {
    if (!attempt) {
        return
    }

    const events = attempt.events.slice(attempt.sent)

    if (!events.length) {
        return
    }

    attempt.sent = attempt.events.length

    const config = document.querySelector(endpointsSelector)
    const url = config?.dataset.diagnosticsUrl

    if (!url) {
        return
    }

    const body = new FormData()
    const csrfName = config.dataset.csrfName
    const csrfInput = csrfName
        ? document.querySelector(`input[name="${csrfName}"]`)
        : null

    body.append(
        'report',
        JSON.stringify({
            id: attempt.id,
            reason: attempt.truncated ? `${reason} (truncated)` : reason,
            ua: navigator.userAgent,
            context: describeContext(),
            events
        })
    )

    if (csrfInput) {
        body.append(csrfName, csrfInput.value)
    }

    try {
        // `sendBeacon` because the most interesting reports are the ones sent while the visitor is
        // already closing the tab on a form that would not move.
        if (!navigator.sendBeacon?.(url, body)) {
            fetch(url, { method: 'POST', body, keepalive: true })
        }
    } catch {
        // Never let a diagnostic break the form.
    }
}

const describeBody = (body) => {
    try {
        const file = body?.get?.('file')

        return file ? `file=${file.name} ${file.size}b` : 'no file in body'
    } catch {
        return 'body unreadable'
    }
}

const watchRequest = (xhr, label) => {
    attempt.requests += 1
    recordUploadEvent('xhr:send', label)

    let lastPercent = -1
    let lastRecordedAt = 0

    xhr.upload?.addEventListener('progress', (event) => {
        if (!attempt) {
            return
        }

        attempt.lastProgressAt = Date.now()

        const percent =
            event.lengthComputable && event.total > 0
                ? Math.round((event.loaded / event.total) * 100)
                : -1
        const now = Date.now()

        // Every progress event would drown the timeline, so only the steps that carry meaning.
        if (
            percent - lastPercent < progressStep &&
            now - lastRecordedAt < progressInterval &&
            percent < 100
        ) {
            return
        }

        lastPercent = percent
        lastRecordedAt = now

        recordUploadEvent(
            'xhr:progress',
            `${percent < 0 ? '?' : percent}% ${event.loaded}/${event.total}b`
        )
    })

    xhr.addEventListener('load', () => {
        recordUploadEvent(
            'xhr:load',
            `status=${xhr.status} ${short(xhr.responseText, 120)}`
        )
    })

    xhr.addEventListener('error', () => {
        recordUploadEvent('xhr:error', `status=${xhr.status}`)
    })

    xhr.addEventListener('timeout', () => {
        recordUploadEvent('xhr:timeout', `after ${xhr.timeout}ms`)
    })

    xhr.addEventListener('abort', () => {
        recordUploadEvent('xhr:abort', `status=${xhr.status}`)
    })
}

// Installed at import time, so it is in place before Formie's module mounts and creates its Uppy.
const installXhrProbe = () => {
    if (XMLHttpRequest.prototype.whwUploadProbe) {
        return
    }

    const { open, send } = XMLHttpRequest.prototype

    XMLHttpRequest.prototype.whwUploadProbe = true

    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
        try {
            if (typeof url === 'string' && url.includes(uploadUrlFragment)) {
                this.whwUploadLabel = `${method} ${url.split(uploadUrlFragment)[1]}`
            }
        } catch {
            // Only the label is lost.
        }

        return open.call(this, method, url, ...rest)
    }

    XMLHttpRequest.prototype.send = function (body) {
        try {
            if (this.whwUploadLabel && attempt) {
                watchRequest(
                    this,
                    `${this.whwUploadLabel} ${describeBody(body)}`
                )
            }
        } catch {
            // Observing must never stop the request itself.
        }

        return send.call(this, body)
    }
}

installXhrProbe()

const onPageHide = () => {
    flushUploadReport('page left with an upload in flight')
}

// `pagehide` is not dependable on a phone: a tab that gets killed while the visitor is in another
// app may never fire it, while `visibilitychange` always comes first. Both end in the same flush,
// and a second one finds nothing left to send — every report is sent once.
const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
        flushUploadReport('page hidden with an upload in flight')
    }
}

/**
 * Per-page wiring: Formie's own events and anything that blows up while an upload is running.
 */
export const initRequestUploadDiagnostics = (scope = document) => {
    const forms = scope.querySelectorAll?.('form[data-formie-form]') ?? []

    forms.forEach((form) => {
        if (form.dataset.whwUploadDiagnostics === 'true') {
            return
        }

        form.dataset.whwUploadDiagnostics = 'true'

        observedFormEvents.forEach((name) => {
            form.addEventListener(name, (event) => {
                // Formie dispatches these through its own emitter: anything thrown in here
                // lands in the middle of a submit.
                try {
                    recordUploadEvent(
                        name,
                        short(describeDetail(event.detail), 120)
                    )
                } catch {
                    recordUploadEvent(name)
                }
            })
        })
    })

    if (document.body.dataset.whwUploadDiagnostics === 'true') {
        return
    }

    document.body.dataset.whwUploadDiagnostics = 'true'

    window.addEventListener('error', (event) => {
        recordUploadEvent('js:error', `${event.message} @ ${event.filename}`)
    })

    window.addEventListener('unhandledrejection', (event) => {
        recordUploadEvent('js:rejection', short(String(event.reason), 120))
    })

    window.addEventListener('offline', () => recordUploadEvent('net:offline'))
    window.addEventListener('online', () => recordUploadEvent('net:online'))
    window.addEventListener('pagehide', onPageHide)
    document.addEventListener('visibilitychange', onVisibilityChange)
}
